import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

const Cart = () => {
  const { cart, removeFromCart, updateCartItem, getCartTotal } = useCart();
  const navigate = useNavigate();

  const handleQuantityChange = async (productId, newQuantity) => {
    if (newQuantity < 1) return;
    try {
      await updateCartItem(productId, newQuantity);
    } catch (error) {
      toast.error('Failed to update quantity');
    }
  };

  const handleRemove = async (productId) => {
    try {
      await removeFromCart(productId);
      toast.success('Item removed from cart');
    } catch (error) {
      toast.error('Failed to remove item');
    }
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4 py-12" data-testid="empty-cart">
        <h1 className="font-heading text-4xl text-forest mb-4">Your Cart is Empty</h1>
        <p className="text-muted-foreground mb-8">Start adding beautiful pieces to your collection</p>
        <Link
          to="/products"
          className="bg-forest text-ivory px-8 py-4 rounded-none hover:bg-gold hover:text-forest transition-colors text-sm uppercase tracking-[0.1em]"
          data-testid="continue-shopping-button"
        >
          Continue Shopping
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8" data-testid="cart-page">
      <div className="max-w-7xl mx-auto">
        <h1 className="font-heading text-4xl sm:text-5xl text-forest mb-8 tracking-tight" data-testid="cart-heading">Shopping Cart</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cart.map((item) => (
              <div
                key={item.product_id}
                className="bg-neutral-warm p-6 rounded-sm flex items-start space-x-4"
                data-testid="cart-item"
              >
                <img
                  src={item.product?.images[0]}
                  alt={item.product?.name}
                  className="w-24 h-32 object-cover rounded-sm"
                />
                <div className="flex-1">
                  <h3 className="font-heading text-xl text-forest mb-1" data-testid="cart-item-name">{item.product?.name}</h3>
                  <p className="text-sm text-muted-foreground mb-2">{item.product?.fabric} • {item.product?.color}</p>
                  <p className="text-lg text-forest" data-testid="cart-item-price">₹{item.product?.price.toLocaleString()}</p>
                  
                  <div className="flex items-center space-x-4 mt-4">
                    <div className="flex items-center space-x-2 border border-gold rounded-sm">
                      <button
                        onClick={() => handleQuantityChange(item.product_id, item.quantity - 1)}
                        className="p-2 hover:bg-ivory transition-colors"
                        data-testid="decrease-quantity-button"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="px-4 text-forest" data-testid="cart-item-quantity">{item.quantity}</span>
                      <button
                        onClick={() => handleQuantityChange(item.product_id, item.quantity + 1)}
                        className="p-2 hover:bg-ivory transition-colors"
                        data-testid="increase-quantity-button"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                    
                    <button
                      onClick={() => handleRemove(item.product_id)}
                      className="text-forest hover:text-gold transition-colors"
                      data-testid="remove-cart-item-button"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-neutral-warm p-6 rounded-sm sticky top-24" data-testid="order-summary">
              <h2 className="text-sm uppercase tracking-[0.2em] text-forest mb-6">Order Summary</h2>
              
              <div className="space-y-4 mb-6">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="text-forest" data-testid="cart-subtotal">₹{getCartTotal().toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Shipping</span>
                  <span className="text-forest">Free</span>
                </div>
                <div className="border-t border-gold pt-4">
                  <div className="flex justify-between">
                    <span className="font-heading text-xl text-forest">Total</span>
                    <span className="font-heading text-xl text-forest" data-testid="cart-total">₹{getCartTotal().toLocaleString()}</span>
                  </div>
                </div>
              </div>

              <button
                onClick={() => navigate('/checkout')}
                className="w-full bg-forest text-ivory px-8 py-4 rounded-none hover:bg-gold hover:text-forest transition-colors text-sm uppercase tracking-[0.1em] mb-4"
                data-testid="proceed-to-checkout-button"
              >
                Proceed to Checkout
              </button>

              <Link
                to="/products"
                className="block text-center text-sm text-forest hover:text-gold underline"
                data-testid="continue-shopping-link"
              >
                Continue Shopping
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
